import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import { cn } from '@/lib/utils';

const loadingIndicatorVariants = cva(
  'flex flex-col items-center justify-center',
  {
    variants: {
      size: {
        sm: 'gap-4',
        md: 'gap-4',
        lg: 'gap-4',
        xl: 'gap-5',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  },
);

const loadingTextVariants = cva(
  'font-medium text-secondary-foreground text-center',
  {
    variants: {
      size: {
        sm: 'text-sm leading-5',
        md: 'text-sm leading-5',
        lg: 'text-lg leading-7',
        xl: 'text-lg leading-7',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  },
);

// Size configurations for the spinner
const SIZE_CONFIG = {
  sm: { dimension: 32, strokeWidth: 4 },
  md: { dimension: 48, strokeWidth: 6 },
  lg: { dimension: 56, strokeWidth: 6 },
  xl: { dimension: 64, strokeWidth: 8 },
} as const;

const LINE_SIMPLE_ARC_RATIO = 0.25;
const LINE_SPINNER_ARC_RATIO = 0.6;
const DOT_CIRCLE_DASH_SPACING = 8.1;
const DOT_CIRCLE_FADE_ARC_RATIO = 331.875 / 360;

export type LoadingIndicatorVariant =
  | 'line-simple'
  | 'line-spinner'
  | 'dot-circle'
  | 'breathing';

export interface LoadingIndicatorProps
  extends React.HTMLAttributes<HTMLOutputElement>,
    VariantProps<typeof loadingIndicatorVariants> {
  /** The visual variant of the loading indicator */
  variant?: LoadingIndicatorVariant;
  /** Whether to show the "Loading..." text */
  showText?: boolean;
  /** Custom loading text */
  text?: string;
  /** Color of the spinner (CSS color value) */
  color?: string;
  /** Track color for line styles */
  trackColor?: string;
}

/** Line Simple Spinner - A simple arc that rotates */
function LineSimpleSpinner({
  size,
  color = 'var(--primary)',
  trackColor = 'var(--border)',
}: {
  size: keyof typeof SIZE_CONFIG;
  color?: string;
  trackColor?: string;
}) {
  const config = SIZE_CONFIG[size];
  const center = config.dimension / 2;
  const radius = (config.dimension - config.strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const arcLength = circumference * LINE_SIMPLE_ARC_RATIO;

  return (
    <svg
      width={config.dimension}
      height={config.dimension}
      viewBox={`0 0 ${config.dimension} ${config.dimension}`}
      className="animate-spin motion-reduce:animate-none"
      style={{ animationDuration: '1s' }}
      aria-hidden="true"
    >
      {/* Background track */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={trackColor}
        strokeWidth={config.strokeWidth}
        strokeLinecap="round"
      />
      {/* Spinning arc */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={config.strokeWidth}
        strokeLinecap="round"
        strokeDasharray={`${arcLength} ${circumference - arcLength}`}
        transform={`rotate(-90 ${center} ${center})`}
      />
    </svg>
  );
}

/** Line Spinner - A solid long arc that rotates */
function LineSpinner({
  size,
  color = 'var(--primary)',
  trackColor = 'var(--background)',
}: {
  size: keyof typeof SIZE_CONFIG;
  color?: string;
  trackColor?: string;
}) {
  const config = SIZE_CONFIG[size];
  const center = config.dimension / 2;
  const radius = (config.dimension - config.strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const arcLength = circumference * LINE_SPINNER_ARC_RATIO;

  return (
    <svg
      width={config.dimension}
      height={config.dimension}
      viewBox={`0 0 ${config.dimension} ${config.dimension}`}
      className="animate-spin motion-reduce:animate-none"
      style={{ animationDuration: '0.8s' }}
      aria-hidden="true"
    >
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={trackColor}
        strokeWidth={config.strokeWidth}
        strokeLinecap="round"
      />
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={config.strokeWidth}
        strokeLinecap="round"
        strokeDasharray={`${arcLength} ${circumference - arcLength}`}
        transform={`rotate(-135 ${center} ${center})`}
      />
    </svg>
  );
}

/** Dot Circle Spinner - A dashed circle with a fade chase effect */
function DotCircleSpinner({
  size,
  color = 'var(--primary)',
}: {
  size: keyof typeof SIZE_CONFIG;
  color?: string;
}) {
  const config = SIZE_CONFIG[size];
  const center = config.dimension / 2;
  const radius = (config.dimension - config.strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dotCount = Math.round(circumference / DOT_CIRCLE_DASH_SPACING);
  const dotRadius = config.strokeWidth / 2;

  return (
    <svg
      width={config.dimension}
      height={config.dimension}
      viewBox={`0 0 ${config.dimension} ${config.dimension}`}
      className="animate-spin motion-reduce:animate-none"
      style={{ animationDuration: '1s' }}
      aria-hidden="true"
    >
      {Array.from({ length: dotCount }).map((_, index) => {
        const progress = index / dotCount;
        const angle = progress * 360 - 90;
        const radian = (angle * Math.PI) / 180;
        const x = center + radius * Math.cos(radian);
        const y = center + radius * Math.sin(radian);
        const opacity =
          progress > DOT_CIRCLE_FADE_ARC_RATIO
            ? 0
            : 1 - progress / DOT_CIRCLE_FADE_ARC_RATIO;

        return (
          <circle
            key={index}
            cx={x}
            cy={y}
            r={dotRadius}
            fill={color}
            opacity={opacity}
          />
        );
      })}
    </svg>
  );
}

/** Breathing Spinner - An organic scale/opacity oscillation that feels alive */
function BreathingSpinner({
  size,
  color = 'var(--primary)',
}: {
  size: keyof typeof SIZE_CONFIG;
  color?: string;
}) {
  const config = SIZE_CONFIG[size];
  const r = config.dimension * 0.35;

  return (
    <svg
      width={config.dimension}
      height={config.dimension}
      viewBox={`0 0 ${config.dimension} ${config.dimension}`}
      aria-hidden="true"
    >
      <circle
        cx={config.dimension / 2}
        cy={config.dimension / 2}
        r={r}
        fill={color}
        opacity={0.15}
      />
      <circle
        cx={config.dimension / 2}
        cy={config.dimension / 2}
        r={r * 0.6}
        fill={color}
        className="delight-breathing"
      />
    </svg>
  );
}

interface LoadingSpinnerProps {
  variant: LoadingIndicatorVariant;
  size: keyof typeof SIZE_CONFIG;
  color?: string;
  trackColor?: string;
}

function LoadingSpinner({
  variant,
  size,
  color,
  trackColor,
}: LoadingSpinnerProps) {
  switch (variant) {
    case 'line-spinner':
      return <LineSpinner size={size} color={color} trackColor={trackColor} />;
    case 'dot-circle':
      return <DotCircleSpinner size={size} color={color} />;
    case 'breathing':
      return <BreathingSpinner size={size} color={color} />;
    default:
      return (
        <LineSimpleSpinner size={size} color={color} trackColor={trackColor} />
      );
  }
}

function LoadingIndicator({
  variant = 'line-simple',
  size = 'sm',
  showText = true,
  text = 'Loading...',
  color,
  trackColor,
  className,
  ...props
}: LoadingIndicatorProps) {
  const effectiveSize = size || 'sm';

  return (
    <output
      data-slot="loading-indicator"
      className={cn(
        loadingIndicatorVariants({ size }),
        'transition-opacity duration-(--duration-fast) ease-enter starting:opacity-0 motion-reduce:transition-none',
        className,
      )}
      aria-live="polite"
      aria-busy="true"
      {...props}
    >
      <LoadingSpinner
        variant={variant}
        size={effectiveSize}
        color={color}
        trackColor={trackColor}
      />
      {showText && (
        <p className={loadingTextVariants({ size })}>
          {text}
          <span className="sr-only"> in progress</span>
        </p>
      )}
    </output>
  );
}

LoadingIndicator.displayName = 'LoadingIndicator';

export {
  BreathingSpinner,
  DotCircleSpinner,
  LineSimpleSpinner,
  LineSpinner,
  LoadingIndicator,
  loadingIndicatorVariants,
  loadingTextVariants,
};
